import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Warning,
  Error,
  CheckCircle,
  Refresh,
  Search,
  Close,
  Assignment,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  positionBreakService,
  PositionBreak,
  BreakSummary,
} from '../services/positionBreakService';
import { fundsApi, Fund } from '../services/fundsApi';
import { LoggerService } from '../services/LoggerService';

const PositionBreaksPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [breaks, setBreaks] = useState<PositionBreak[]>([]);
  const [summary, setSummary] = useState<BreakSummary | null>(null);
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [funds, setFunds] = useState<Fund[]>([]);
  const [breakTypeFilter, setBreakTypeFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedBreak, setSelectedBreak] = useState<PositionBreak | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadFunds();
  }, []);

  useEffect(() => {
    if (selectedFund) {
      loadBreaks();
      loadSummary();
    }
  }, [selectedFund, breakTypeFilter, severityFilter, statusFilter, dateFrom, dateTo]);

  const loadFunds = async () => {
    try {
      const fundsResponse = await fundsApi.getFunds({}, 1, 100);
      const fundsData = fundsResponse.data || fundsResponse.items || [];
      setFunds(fundsData);
      if (fundsData.length > 0 && !selectedFund) {
        setSelectedFund(fundsData[0].id);
      }
    } catch (err: any) {
      LoggerService.error('Error loading funds:', err);
      setError('Failed to load funds');
    }
  };

  const loadBreaks = async () => {
    if (!selectedFund) return;

    try {
      setLoading(true);
      setError(null);

      const breaksData = await positionBreakService.getPositionBreaks({
        fund_id: selectedFund,
        break_type: breakTypeFilter !== 'all' ? breakTypeFilter : undefined,
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });

      setBreaks(breaksData.breaks);
    } catch (err: any) {
      LoggerService.error('Error loading position breaks:', err);
      setError(err.response?.data?.detail || 'Failed to load position breaks');
      enqueueSnackbar('Failed to load position breaks', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!selectedFund) return;

    try {
      const summaryData = await positionBreakService.getBreakSummary(selectedFund);
      setSummary(summaryData);
    } catch (err: any) {
      LoggerService.error('Error loading break summary:', err);
    }
  };

  const handleDetectBreaks = async () => {
    if (!selectedFund) return;

    try {
      setLoading(true);
      const result = await positionBreakService.detectBreaks(selectedFund);
      enqueueSnackbar(
        `Detected ${result.breaks_detected} position break(s)`,
        { variant: 'success' }
      );
      await loadBreaks();
      await loadSummary();
    } catch (err: any) {
      LoggerService.error('Error detecting breaks:', err);
      enqueueSnackbar('Failed to detect breaks', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResolveBreak = async () => {
    if (!selectedBreak) return;

    try {
      await positionBreakService.resolveBreak(
        selectedBreak.id,
        resolutionNotes
      );
      enqueueSnackbar('Break resolved successfully', { variant: 'success' });
      setResolveDialogOpen(false);
      setResolutionNotes('');
      setSelectedBreak(null);
      await loadBreaks();
      await loadSummary();
    } catch (err: any) {
      LoggerService.error('Error resolving break:', err);
      enqueueSnackbar('Failed to resolve break', { variant: 'error' });
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'investigating':
        return 'warning';
      case 'open':
        return 'error';
      case 'false_positive':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading && !breaks.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Position Breaks Monitoring
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Fund</InputLabel>
                <Select
                  value={selectedFund}
                  onChange={(e) => setSelectedFund(e.target.value)}
                  label="Fund"
                >
                  {funds.map((fund) => (
                    <MenuItem key={fund.id} value={fund.id}>
                      {fund.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={breakTypeFilter}
                  onChange={(e) => setBreakTypeFilter(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="reconciliation">Reconciliation</MenuItem>
                  <MenuItem value="valuation">Valuation</MenuItem>
                  <MenuItem value="settlement">Settlement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  label="Severity"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="investigating">Investigating</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="false_positive">False Positive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Refresh />}
                onClick={handleDetectBreaks}
                disabled={loading || !selectedFund}
              >
                Detect Breaks
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Breaks
                </Typography>
                <Typography variant="h4">{summary.total_breaks}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Open Breaks
                </Typography>
                <Typography variant="h4" color="error">
                  {summary.open_breaks}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Critical/High
                </Typography>
                <Typography variant="h4" color="error">
                  {summary.by_severity.critical + summary.by_severity.high}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Resolved
                </Typography>
                <Typography variant="h4" color="success.main">
                  {summary.resolved_breaks}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Breaks Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Position Breaks
          </Typography>
          {breaks.length === 0 ? (
            <Alert severity="info">No position breaks found for the selected filters.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Security</TableCell>
                    <TableCell align="right">Expected</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell align="right">Difference</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breaks.map((breakItem) => (
                    <TableRow key={breakItem.id}>
                      <TableCell>{formatDate(breakItem.break_date)}</TableCell>
                      <TableCell>
                        <Chip label={breakItem.break_type} size="small" />
                      </TableCell>
                      <TableCell>
                        {breakItem.security_name || breakItem.security_id || 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(breakItem.expected_value)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(breakItem.actual_value)}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          color: breakItem.difference >= 0 ? 'green' : 'red',
                          fontWeight: 'bold',
                        }}
                      >
                        {formatCurrency(breakItem.difference)} (
                        {breakItem.difference_percentage.toFixed(2)}%)
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={breakItem.severity}
                          size="small"
                          color={getSeverityColor(breakItem.severity) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={breakItem.status}
                          size="small"
                          color={getStatusColor(breakItem.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        {breakItem.status !== 'resolved' && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBreak(breakItem);
                              setResolveDialogOpen(true);
                            }}
                            color="primary"
                          >
                            <Assignment />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => {
          setResolveDialogOpen(false);
          setResolutionNotes('');
          setSelectedBreak(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Resolve Position Break
          <IconButton
            onClick={() => {
              setResolveDialogOpen(false);
              setResolutionNotes('');
              setSelectedBreak(null);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedBreak && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Break Type: {selectedBreak.break_type}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Difference: {formatCurrency(selectedBreak.difference)} (
                {selectedBreak.difference_percentage.toFixed(2)}%)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Resolution Notes"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                margin="normal"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResolveDialogOpen(false);
              setResolutionNotes('');
              setSelectedBreak(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResolveBreak}
            variant="contained"
            disabled={!resolutionNotes.trim()}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PositionBreaksPage;

